import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UserPresence {
  userId: string;
  username: string;
  displayName?: string;
  cursor: { x: number; y: number };
  viewport: { x: number; y: number; zoom: number };
  color: string;
  lastSeen: number;
  isActive: boolean;
  isFollowed?: boolean; // User is followed by current user
  isFollowing?: boolean; // User follows current user
  isMutual?: boolean; // Mutual following relationship
  profileImage?: string; // User profile image URL
}

interface CanvasPresenceHook {
  activeUsers: UserPresence[];
  updateCursor: (x: number, y: number) => void;
  updateViewport: (x: number, y: number, zoom: number) => void;
  userCount: number;
  followedUsers: UserPresence[]; // Users followed by current user
  mutualFollowers: UserPresence[]; // Mutual followers
  goToUser: (userId: string) => void; // Navigate to a user's location
}

const USER_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', 
  '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899',
  '#f59e0b', '#10b981', '#6366f1', '#8b5cf6'
];

export const useCanvasPresence = (canvasId: string = 'main'): CanvasPresenceHook => {
  const { user } = useAuth();
  const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);
  const [followedUsers, setFollowedUsers] = useState<UserPresence[]>([]);
  const [mutualFollowers, setMutualFollowers] = useState<UserPresence[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const userColorRef = useRef<string>('');
  const [followRelations, setFollowRelations] = useState<Map<string, {isFollowed: boolean, isFollowing: boolean}>>(new Map());
  const activeUsersRef = useRef<UserPresence[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    activeUsersRef.current = activeUsers;
  }, [activeUsers]);

  // Assign consistent color to user
  const getUserColor = useCallback((userId: string) => {
    if (!userColorRef.current) {
      const hash = userId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      userColorRef.current = USER_COLORS[Math.abs(hash) % USER_COLORS.length];
    }
    return userColorRef.current;
  }, []);

  // Update cursor position
  const updateCursor = useCallback((x: number, y: number) => {
    if (!user || !channelRef.current) return;
    
    const now = Date.now();
    if (now - lastUpdateRef.current < 50) return; // Throttle to 20fps
    
    lastUpdateRef.current = now;
    
    channelRef.current.send({
      type: 'broadcast',
      event: 'cursor_move',
      payload: {
        userId: user.id,
        username: user.username,
        displayName: user.name,
        cursor: { x, y },
        color: getUserColor(user.id),
        timestamp: now
      }
    });
  }, [user, getUserColor]);

  // Update viewport position
  const updateViewport = useCallback((x: number, y: number, zoom: number) => {
    if (!user || !channelRef.current) return;
    
    channelRef.current.send({
      type: 'broadcast',
      event: 'viewport_change',
      payload: {
        userId: user.id,
        viewport: { x, y, zoom },
        timestamp: Date.now()
      }
    });
  }, [user]);

  // Fetch followers and following data
  useEffect(() => {
    if (!user) return;
    
    const fetchFollowRelationships = async () => {
      try {
        // Fetch users that the current user follows
        const { data: following, error: followingError } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user.id);
          
        // Fetch users that follow the current user
        const { data: followers, error: followersError } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('following_id', user.id);
          
        if (followingError) console.error('Error fetching following:', followingError);
        if (followersError) console.error('Error fetching followers:', followersError);
        
        if (following && followers) {
          // Create maps for quick lookup
          const followingMap = new Map(following.map(f => [f.following_id, true]));
          const followersMap = new Map(followers.map(f => [f.follower_id, true]));
          
          // Build relationships map
          const relations = new Map<string, {isFollowed: boolean, isFollowing: boolean}>();
          
          // Add all following
          following.forEach(f => {
            relations.set(f.following_id, {
              isFollowed: true,
              isFollowing: followersMap.has(f.following_id)
            });
          });
          
          // Add all followers that weren't added yet
          followers.forEach(f => {
            if (!relations.has(f.follower_id)) {
              relations.set(f.follower_id, {
                isFollowed: false,
                isFollowing: true
              });
            }
          });
          
          setFollowRelations(relations);
        }
      } catch (error) {
        console.error('Error fetching follow relationships:', error);
      }
    };
    
    fetchFollowRelationships();
  }, [user]);
  
  // Update filtered user lists when followRelations change
  useEffect(() => {
    // Use ref to access current activeUsers without adding dependency
    const currentActiveUsers = activeUsersRef.current;
    
    // Filter followed users
    const followed = currentActiveUsers.filter(u => 
      followRelations.get(u.userId)?.isFollowed
    );
    setFollowedUsers(followed);
    
    // Filter mutual followers
    const mutual = currentActiveUsers.filter(u => 
      followRelations.get(u.userId)?.isFollowed && 
      followRelations.get(u.userId)?.isFollowing
    );
    setMutualFollowers(mutual);
    
    // Update user presence with follower info - use functional update to avoid dependency loop
    setActiveUsers(prev => {
      // Only update if relations actually changed for these users
      const updated = prev.map(u => {
        const relation = followRelations.get(u.userId);
        if (relation) {
          // Check if data actually changed to avoid unnecessary updates
          if (u.isFollowed === relation.isFollowed && 
              u.isFollowing === relation.isFollowing &&
              u.isMutual === (relation.isFollowed && relation.isFollowing)) {
            return u; // No change, return same reference
          }
          return {
            ...u,
            isFollowed: relation.isFollowed,
            isFollowing: relation.isFollowing,
            isMutual: relation.isFollowed && relation.isFollowing
          };
        }
        return u;
      });
      return updated;
    });
  }, [followRelations]); // ✅ Only depends on followRelations!
  
  // Navigate to another user's location
  const goToUser = useCallback((userId: string) => {
    const userToFollow = activeUsers.find(u => u.userId === userId);
    if (!userToFollow || !userToFollow.viewport) return;
    
    // Set viewport to match the user's location
    const { viewport } = userToFollow;
    
    // Create a smooth animation to the location (handled in FomoCanvas component)
    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'follow_user',
        payload: {
          userId: user?.id,
          targetUserId: userId,
          viewport: viewport,
          timestamp: Date.now()
        }
      });
    }
    
    // Return the viewport to allow the component to animate to it
    return viewport;
  }, [activeUsers, user?.id]);
  
  // Initialize presence channel
  useEffect(() => {
    if (!user) return;

    const channelName = `canvas_presence:${canvasId}`;
    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { self: false },
        presence: { key: user.id }
      }
    });

    channelRef.current = channel;

    // Handle presence state
    channel
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const users: UserPresence[] = [];
        
        Object.entries(presenceState).forEach(([userId, presences]) => {
          const presence = presences[0] as any;
          if (presence && userId !== user.id) {
            users.push({
              userId,
              username: presence.username || 'Anonymous',
              displayName: presence.displayName,
              cursor: presence.cursor || { x: 0, y: 0 },
              viewport: presence.viewport || { x: 0, y: 0, zoom: 1 },
              color: getUserColor(userId),
              lastSeen: presence.lastSeen || Date.now(),
              isActive: Date.now() - (presence.lastSeen || 0) < 5000
            });
          }
        });
        
        setActiveUsers(users);
      })
      .on('broadcast', { event: 'cursor_move' }, ({ payload }) => {
        if (payload.userId === user.id) return;
        
        setActiveUsers(prev => {
          const updated = prev.map(u => 
            u.userId === payload.userId 
              ? { 
                  ...u, 
                  cursor: payload.cursor,
                  lastSeen: payload.timestamp,
                  isActive: true
                }
              : u
          );
          
          // Add new user if not exists
          if (!updated.find(u => u.userId === payload.userId)) {
            updated.push({
              userId: payload.userId,
              username: payload.username || 'Anonymous',
              displayName: payload.displayName,
              cursor: payload.cursor,
              viewport: { x: 0, y: 0, zoom: 1 },
              color: payload.color || getUserColor(payload.userId),
              lastSeen: payload.timestamp,
              isActive: true
            });
          }
          
          return updated;
        });
      })
      .on('broadcast', { event: 'viewport_change' }, ({ payload }) => {
        if (payload.userId === user.id) return;
        
        setActiveUsers(prev => 
          prev.map(u => 
            u.userId === payload.userId 
              ? { ...u, viewport: payload.viewport, lastSeen: payload.timestamp }
              : u
          )
        );
      });

    // Generate random spawn location based on user ID
    const getRandomSpawnLocation = () => {
      // Use a hash of the user ID to generate a consistent but random-appearing spawn point
      // This ensures users always spawn in the same location on refresh
      const hashCode = user.id.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      
      // Use the hash to generate coordinates within a reasonable range (-5000 to 5000)
      const x = ((hashCode & 0xFFFF) % 10000) - 5000;
      const y = ((hashCode >> 16) % 10000) - 5000;
      
      return { x, y };
    };
    
    // Get spawn location
    const spawnLocation = getRandomSpawnLocation();
    
    // Track own presence with the spawn location
    channel.track({
      userId: user.id,
      username: user.username,
      displayName: user.name,
      cursor: spawnLocation, // Use spawn location as initial cursor position
      viewport: { x: spawnLocation.x, y: spawnLocation.y, zoom: 1 }, // Also set viewport to spawn location
      lastSeen: Date.now(),
      isFollowed: false, // Initialize followed status
      isMutual: false    // Initialize mutual follower status
    });

    channel.subscribe((status) => {
      console.log('Canvas presence channel status:', status);
    });

    // Cleanup inactive users
    const cleanupInterval = setInterval(() => {
      setActiveUsers(prev => 
        prev.map(user => ({
          ...user,
          isActive: Date.now() - user.lastSeen < 5000
        })).filter(user => Date.now() - user.lastSeen < 30000) // Remove after 30s
      );
    }, 2000);

    return () => {
      clearInterval(cleanupInterval);
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [user, canvasId, getUserColor]);

  return {
    activeUsers,
    updateCursor,
    updateViewport,
    userCount: activeUsers.length + 1, // +1 for current user
    followedUsers,
    mutualFollowers,
    goToUser
  };
};
